/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Drawer from "@mui/material/Drawer";
import style from "../../../styles/common/formModal.module.css";
import EditDialog from "../../../Components/Common/EditDialog";
import FileField from "../../../Components/Masters/FileField";
import formatTitleWithOptional from "../../Scope1/OptionalFeild";
import { useUser } from "../../../context/AuthContext";
import Multiline from "../../../Components/Masters/Multiline";
import countries from "../../../Pages/Masters/Roles and Permissions/Country.json";

const Form = ({
  isOpen,
  onClose,
  onSubmit,
  header,
  initialData,
  isEditing,
  defaultValue,
  id,
}) => {
  const { user } = useUser();

  // Handle both object format (from API) and string format (from table)
  let quantitySoldObj = {};
  if (typeof initialData?.quantitySold === 'object' && initialData?.quantitySold !== null) {
    quantitySoldObj = initialData.quantitySold;
  } else if (initialData?.quantitySold && typeof initialData.quantitySold === 'string') {
    // If it's a string like "10 KG", try to parse it
    const parts = initialData.quantitySold.trim().split(' ');
    if (parts.length >= 2) {
      quantitySoldObj = {
        value: parseFloat(parts[0]) || "",
        unit: parts.slice(1).join(' ') || "KG"
      };
    }
  }

  const initialFormData = {
    productName: initialData?.productName || "",
    productCategory: initialData?.productCategory || "",
    vendorName: initialData?.vendorName || "",
    quantitySold: quantitySoldObj?.value || "",
    quantitySoldUnit: quantitySoldObj?.unit || "KG",
    locationOfProcessing: initialData?.locationOfProcessing || "",
    processingMethodUsed: initialData?.processingMethodUsed || "",
    energyConsumptionForProcessing: initialData?.energyConsumptionForProcessing || "",
    energySourceForProcessing: initialData?.energySourceForProcessing || "",
    materialsAddedDuringProcessing: initialData?.materialsAddedDuringProcessing || "",
    wasteGeneratedDuringProcessing: initialData?.wasteGeneratedDuringProcessing || "",
    waterUsedInProcessing: initialData?.waterUsedInProcessing || "",
    remark: initialData?.remark || "",
    documents: initialData?.documents || [],
    editRemarkReason: initialData?.editRemarkReason || "",
  };

  const [editDialog, setEditDialog] = useState(false);
  const fieldConfig = [
    {
      label: "Product Name",
      name: "productName",
      type: "text",
      placeholder: "Enter product name",
      required: true,
    },
    {
      label: "Product Category",
      name: "productCategory",
      type: "select",
      placeholder: "Tap to select",
      required: true,
      options: [
        "Category 1",
        "Category 2",
        "Category 3",
        "Category 4",
        "Category 5",
      ],
    },
    {
      label: "Vendor Name",
      name: "vendorName",
      type: "text",
      placeholder: "Enter vendor name",
      required: true,
    },
    {
      label: "Quantity Sold",
      name: "quantitySold",
      type: "text",
      placeholder: "Enter quantity",
      required: true,
      unitFieldName: "quantitySoldUnit",
      unitOptions: ["KG"],
      numericOnly: true,
    },
    {
      label: "Location of Processing",
      name: "locationOfProcessing",
      type: "select",
      placeholder: "Tap to select",
      required: false,
      options: countries?.countries?.map((country) => country.name),
    },
    {
      label: "Processing Method Used",
      name: "processingMethodUsed",
      type: "text",
      placeholder: "Enter processing method",
      required: true,
    },
    {
      label: "Energy Consumption for Processing",
      name: "energyConsumptionForProcessing",
      type: "text",
      placeholder: "Enter energy consumption",
      required: true,
      numericOnly: true,
    },
    {
      label: "Energy Source for Processing",
      name: "energySourceForProcessing",
      type: "text",
      placeholder: "Enter energy source",
      required: true,
    },
    {
      label: "Materials Added During Processing",
      name: "materialsAddedDuringProcessing",
      type: "text",
      placeholder: "Enter materials",
      required: false,
    },
    {
      label: "Waste Generated During Processing",
      name: "wasteGeneratedDuringProcessing",
      type: "text",
      placeholder: "Enter waste generated",
      required: false,
    },
    {
      label: "Water Used in Processing (Liters)",
      name: "waterUsedInProcessing",
      type: "text",
      placeholder: "Enter water used",
      required: false,
      numericOnly: true,
    },
    {
      label: "Remark",
      name: "remark",
      type: "multiline",
      placeholder: "Enter remarks",
      required: false,
    },
    {
      name: "editRemarkReason",
      type: "multlyline",
      placeholder: "Additional edit remarks",
      require: true,
    },
    {
      name: "document",
      type: "docs",
      placeholder: "Enter Document",
    },
  ];

  const initialErrors = {};

  const [formData, setFormData] = useState(initialFormData);
  const [formData2, setFormData2] = useState(initialFormData);
  const [errors, setErrors] = useState(initialErrors);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});

  useEffect(() => {
    setUploadedFiles(initialData?.documents || []);
    setFormData(initialFormData);
    setFormData2(initialFormData);
  }, [id, initialData]);

  const handleExitAgree = () => {
    setEditDialog(false);
    setErrors({});
    setFormData(initialFormData);
    setFormData2(initialFormData);
    onClose();
  };

  const validateFormData = () => {
    const newErrors = {};
    fieldConfig.forEach((field) => {
      if (!field.required) {
        return;
      }

      const value = formData[field.name];
      if (!value && field.type !== "docs") {
        newErrors[field.name] = `${field.label} is required.`;
      }

      if (field.type === "number" && value && value < 0) {
        newErrors[field.name] = `${field.label} must be a positive number.`;
      }

      if (field.type === "date" && value && isNaN(new Date(value).getTime())) {
        newErrors[field.name] = `${field.label} is an invalid date.`;
      }
      if (formData?.editRemarkReason === "") {
        if (user?.endUser && isEditing) {
          newErrors.editRemarkReason = "Edit remark is required";
        }
      }
    });
    setErrors(newErrors);
    return Object.values(newErrors).every((error) => error === "");
  };

  const handleClose = () => {
    const isFormDataUnchanged = Object.keys(formData).every((key) => {
      const value1 = formData[key];
      const value2 = formData2[key];
      if (
        typeof value1 === "object" &&
        value1 !== null &&
        typeof value2 === "object" &&
        value2 !== null
      ) {
        return JSON.stringify(value1) === JSON.stringify(value2);
      }
      return value1 === value2;
    });

    if (isFormDataUnchanged) {
      handleExitAgree();
      setUploadedFiles([]);
      setFormData(initialFormData);
    } else {
      setUploadedFiles([]);
      setFormData(initialFormData);
      setEditDialog(true);
    }
  };

  const handleSubmit = () => {
    if (!validateFormData()) return;

    let formDataToSubmit = {
      ...formData,
      quantitySold: {
        value: formData.quantitySold ? parseFloat(formData.quantitySold) : null,
        unit: formData.quantitySoldUnit || "KG",
      },
      documents: uploadedFiles,
    };

    // Remove temporary fields
    delete formDataToSubmit.quantitySoldUnit;

    if (user?.endUser && isEditing) {
      formDataToSubmit.requestStatus = "edit";
      formDataToSubmit.requestMode = true;
      formDataToSubmit.approvalStatus = "Pending";
    }
    onSubmit(formDataToSubmit, isEditing);
    setUploadedFiles([]);
  };

  const handleChange = (field, type) => (event) => {
    const { value } = event.target;

    const fieldConfigItem = fieldConfig.find((f) => f.name === field);
    const isNumericField = type === "number" || fieldConfigItem?.numericOnly;

    if (isNumericField && value !== "" && !/^\d+(\.\d+)?$/.test(value)) return;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  const handleMultiSelectChange = (field) => (event) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [field]: typeof value === "string" ? value.split(",") : value,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  return (
    <Drawer
      sx={{
        backgroundColor: "transparent",
        "& .MuiDrawer-paper": {
          backgroundColor: "transparent",
          boxShadow: "none",
          height: "100vh",
          minHeight: "100vh",
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
      anchor="right"
      open={isOpen}
      onClose={handleClose}
    >
      <Box
        sx={{
          width: "26rem",
          display: "flex",
          justifyContent: "space-between",
          gap: "1rem",
        }}
      >
        {editDialog && (
          <EditDialog {...{ editDialog, setEditDialog, handleExitAgree }} />
        )}
        <Box
          sx={{
            backgroundColor: "#fff",
            height: "20px",
            padding: "13px",
            margin: "30px 0 0 0",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "80%",
            width: "5%",
            zIndex: "1",
          }}
        >
          <IconButton onClick={handleClose} sx={{ color: "black" }}>
            <CloseIcon sx={{ fontSize: "1.2rem" }} />
          </IconButton>
        </Box>
        <Box className={style.main_div}>
          <Typography
            id="modal-title"
            variant="h6"
            align="left"
            sx={{ textDecoration: "underline", fontWeight: "bold", mb: 2 }}
          >
            {header}
          </Typography>
          <form>
            {fieldConfig.map((field) => {
              return (
                <div key={field.name} style={{ margin: "1rem 0" }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", mt: 1 }}
                  >
                    {formatTitleWithOptional(field.label)}
                  </Typography>

                  {user?.endUser && isEditing && field.type === "multlyline" ? (
                    <Multiline
                      title={formatTitleWithOptional("Edit Remark Reason")}
                      placeholder="Additional edit remarks"
                      fieldName="editRemarkReason"
                      value={formData.editRemarkReason}
                      handleChange={handleChange("editRemarkReason")}
                      errors={errors.editRemarkReason}
                    />
                  ) : null}

                  {field.unitFieldName ? (
                    <Box sx={{ display: "flex", gap: "0.75rem" }}>
                      <TextField
                        type={field.type === "number" ? "number" : "text"}
                        size="small"
                        fullWidth
                        placeholder={field.placeholder || ""}
                        value={formData[field.name] || ""}
                        onChange={handleChange(field?.name, field?.type)}
                        error={Boolean(errors[field.name])}
                        helperText={
                          <Typography
                            variant="body2"
                            color="#D34040"
                            sx={{ marginLeft: "-0.8rem" }}
                          >
                            {errors[field.name] || ""}
                          </Typography>
                        }
                      />
                      <TextField
                        select
                        size="small"
                        sx={{ width: "42%" }}
                        value={formData[field.unitFieldName] || ""}
                        onChange={handleChange(field.unitFieldName, "select")}
                        error={Boolean(errors[field.unitFieldName])}
                        helperText={
                          <Typography
                            variant="body2"
                            color="#D34040"
                            sx={{ marginLeft: "-0.8rem" }}
                          >
                            {errors[field.unitFieldName] || ""}
                          </Typography>
                        }
                        SelectProps={{ displayEmpty: true }}
                      >
                        <MenuItem value="" disabled>
                          Unit
                        </MenuItem>
                        {field.unitOptions?.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                  ) : field.type === "select" ? (
                    <TextField
                      select
                      size="small"
                      fullWidth
                      SelectProps={{
                        MenuProps: {
                          PaperProps: {
                            sx: {
                              maxWidth: "200px",
                              maxHeight: "200px",
                            },
                          },
                        },
                      }}
                      value={formData[field?.name] || "Tap to select"}
                      onChange={handleChange(field?.name)}
                      error={Boolean(errors[field.name])}
                      helperText={
                        <Typography
                          variant="body2"
                          color="#D34040"
                          sx={{ marginLeft: "-0.8rem" }}
                        >
                          {errors[field.name] || ""}
                        </Typography>
                      }
                    >
                      <MenuItem value="Tap to select" disabled>
                        {defaultValue || "Tap to select"}
                      </MenuItem>
                      {field.options?.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : field.type === "text" ||
                    field.type === "date" ||
                    field.type === "number" ? (
                    <TextField
                      type={field.type === "date" ? "date" : field.type === "number" ? "number" : "text"}
                      size="small"
                      fullWidth
                      placeholder={field.placeholder || ""}
                      value={formData[field.name] || field.defaultValue || ""}
                      onChange={handleChange(field?.name, field?.type)}
                      error={Boolean(errors[field.name])}
                      helperText={
                        <Typography
                          variant="body2"
                          color="#D34040"
                          sx={{ marginLeft: "-0.8rem" }}
                        >
                          {errors[field.name] || ""}
                        </Typography>
                      }
                      inputProps={
                        field.type === "date"
                          ? { max: new Date().toISOString().split("T")[0] }
                          : {}
                      }
                    />
                  ) : field.type === "multiline" ? (
                    <TextField
                      multiline
                      rows={4}
                      size="small"
                      fullWidth
                      placeholder={field.placeholder || ""}
                      value={formData[field.name] || ""}
                      onChange={handleChange(field?.name, field?.type)}
                      error={Boolean(errors[field.name])}
                      helperText={
                        <Typography
                          variant="body2"
                          color="#D34040"
                          sx={{ marginLeft: "-0.8rem" }}
                        >
                          {errors[field.name] || ""}
                        </Typography>
                      }
                    />
                  ) : field.type === "docs" ? (
                    <FileField
                      title={formatTitleWithOptional(
                        "Upload Documents (optional)"
                      )}
                      name={field.name}
                      errors={errors[field.name]}
                      fileUrls={uploadedFiles}
                      progress={uploadProgress[field.name] || []}
                      {...{
                        setUploadedFiles,
                        setUploadProgress,
                        setErrors,
                      }}
                    />
                  ) : null}
                </div>
              );
            })}
          </form>

          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{ backgroundColor: "#1C486B", textTransform: "none" }}
          >
            Submit
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Form;
